import { formatDayMonthYear } from "@/common/helpers/date-format.util";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { CampaignSummary, MyStatusAssetResponse } from "@amzn/hardware-self-service-api-model";
import type {
  DirectReportStatus,
  MyStatus,
  PickupAddress,
} from "@/services/hardware-self-service/hardware-self-service.types";
import { DIGITS_ONLY } from "./campaigns.constants";

const EMPTY_PICKUP_ADDRESS: PickupAddress = {
  fullName: "",
  countryCode: "",
  phoneNumber: "",
  addressLine1: "",
  addressLine2: "",
  country: "",
  stateProvince: "",
  city: "",
  postalCode: "",
};

/** `hasTargetSubmittedAResponse` tracks the report's *own* submission, so a report the manager answered on behalf of stays in the list. */
export function needsManagerAttention(report: DirectReportStatus): boolean {
  return (
    Boolean(report.status.campaign) && report.status.scope?.hasTargetSubmittedAResponse !== true
  );
}

export function getReportsNeedingAttention(reports: DirectReportStatus[]): DirectReportStatus[] {
  return reports.filter(needsManagerAttention);
}

/**
 * The escalation deadline is `startDate + escalationDays`, formatted from the
 * date's own calendar components so a `YYYY-MM-DD` string is not reinterpreted
 * as UTC midnight and shifted a day earlier in behind-UTC time zones.
 */
export function formatCampaignEscalationDeadline(campaign?: CampaignSummary): string {
  if (!campaign?.startDate || campaign.escalationDays === undefined) {
    return getString("campaigns.unknown");
  }
  const [year, month, day] = campaign.startDate.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) {
    return getString("campaigns.unknown");
  }
  const escalation = new Date(year, month - 1, day + campaign.escalationDays);
  return formatDayMonthYear(escalation);
}

/** Seeds the address form from a previously submitted address, mirroring how responses and remarks are pre-filled. */
export function buildInitialPickupAddress(
  existingAddress?: MyStatus["pickupAddress"]
): PickupAddress {
  if (!existingAddress) {
    return EMPTY_PICKUP_ADDRESS;
  }
  return {
    fullName: existingAddress.fullName ?? "",
    countryCode: existingAddress.countryCode ?? "",
    phoneNumber: existingAddress.phoneNumber ?? "",
    addressLine1: existingAddress.addressLine1 ?? "",
    addressLine2: existingAddress.addressLine2 ?? "",
    country: existingAddress.country ?? "",
    stateProvince: existingAddress.stateProvince ?? "",
    city: existingAddress.city ?? "",
    postalCode: existingAddress.postalCode ?? "",
  };
}

/**
 * Content-based identity for the server snapshot the form seeds from.
 * my-status hands the form a freshly allocated responses object on every render, so only the
 * content can distinguish a genuine refetch from an ordinary re-render.
 */
export function buildServerSnapshotSignature(
  existingResponses: Record<string, MyStatusAssetResponse>,
  existingPickupAddress?: MyStatus["pickupAddress"]
): string {
  return JSON.stringify([existingResponses, existingPickupAddress ?? null]);
}

/** `addressLine2` is the only optional field in the model, so every other field must be filled. */
export function isPickupAddressComplete(address: PickupAddress): boolean {
  return Boolean(
    address.fullName &&
      address.countryCode &&
      address.phoneNumber &&
      address.addressLine1 &&
      address.country &&
      address.stateProvince &&
      address.city &&
      address.postalCode
  );
}

/** The number is stored and dialled unformatted, so separators and letters are rejected. */
export function isPhoneNumberValid(phoneNumber: string): boolean {
  return DIGITS_ONLY.test(phoneNumber.trim());
}

/** HSS checks that every required member is present but not that the phone number is dialable. */
export function isPickupAddressValid(address: PickupAddress): boolean {
  return isPickupAddressComplete(address) && isPhoneNumberValid(address.phoneNumber);
}
